import { classify } from "~/features/audience"
import { formatDate, formatMetaLine } from "~/shared/lib/formatting"
import { accessDestinationLabel, accessTargetFor } from "./accessTarget"
import { canManageAccess, manageLicenseUrl, resourceRedirectUrl } from "./manageAccess"
import type { AccessState, AccessWindow, EntitlementItem, EntitlementNode } from "./types"

/**
 * Active / expired / upcoming is derived here rather than read from the graph.
 * A server-side `isExpired` boolean goes stale inside a cached response, and the
 * per-resolver cache on this API runs at a 900s TTL. MLC derives it client-side
 * too (`utils/tableCellHelpers.js#isDisabled`).
 *
 * A NULL `viewUntil` means unbounded, NOT expired — MLC's default reads a
 * missing end date as "not expired", and reading it the other way would hide
 * content the customer owns (FINDINGS.md gap 23).
 */
export const entitlementState = (
  viewOn: string | null | undefined,
  viewUntil: string | null | undefined,
  now: Date = new Date(),
): AccessState => {
  const start = viewOn ? new Date(viewOn) : undefined
  const end = viewUntil ? new Date(viewUntil) : undefined

  if (end && !Number.isNaN(end.getTime()) && end < now) {
    return "expired"
  }
  if (start && !Number.isNaN(start.getTime()) && start > now) {
    return "upcoming"
  }

  return "active"
}

export const toAccessWindow = (node: EntitlementNode): AccessWindow => {
  const state = entitlementState(node.viewOn, node.viewUntil)

  return {
    state,
    viewOn: formatDate(node.viewOn),
    viewUntil: formatDate(node.viewUntil),
    orderedOn: formatDate(node.orderedOn),
    manageHref: canManageAccess(node.license, node.distributionChannelCodes, state === "expired")
      ? manageLicenseUrl(node.category, node.license?.id)
      : undefined,
  }
}

export const toEntitlementItem = (node: EntitlementNode): EntitlementItem => ({
  id: node.id,
  itemNumber: node.itemNumber,
  /**
   * Observed on UAT: rows whose itemName is just the item number, i.e. missing
   * product metadata (FINDINGS.md Finding D). Showing the number as a title
   * would look like a bug; saying so is more useful.
   */
  title: node.title && node.title !== node.itemNumber ? node.title : `Untitled item ${node.itemNumber}`,
  category: node.category,
  audience: classify({ title: node.title }),
  /**
   * Ordered On only. The item number was here while the join was the question;
   * it is a commerce identifier, not something a customer reads, and `itemNumber`
   * is still on the item for anything that needs it.
   */
  meta: node.orderedOn ? `Ordered on ${formatDate(node.orderedOn)}` : undefined,
  imageUrl: node.imageUrl ?? undefined,
  window: toAccessWindow(node),
  /**
   * Three sources, in order of how much we actually know:
   *
   * 1. The row's own target, when commerce puts one there — memberships do.
   * 2. The destination derived from the seat's distribution channel
   *    (`accessTarget.ts`), which is the common case and the only one that
   *    survives a cold tab.
   * 3. myLifeway's redirect page, for downloads — whose URL is signed and
   *    short-lived, so click-time resolution is the correct mechanism — and for
   *    anything this build does not recognise.
   *
   * None of them costs a request. `accessTargets` stays out of the list query,
   * which is what would make it N+1 (proposal 03 §4).
   */
  accessHref:
    node.accessUrl ??
    accessTargetFor(node.category, node.distributionChannelCodes, node.itemNumber) ??
    resourceRedirectUrl(node.itemNumber),
  accessLabel: accessDestinationLabel(node.category, node.distributionChannelCodes),
})

/**
 * Annotates rows that share an item number with `Seat n of m`.
 *
 * Two seats on the same product are a real condition, not a duplicate: a UAT
 * account carries `005831467` twice under different licences, and because both
 * rows lack product metadata they render identically and read as a paging bug
 * (FINDINGS.md Finding E). Saying which seat is which is cheaper than explaining
 * it every time someone loads the next page.
 *
 * Counts cover the rows loaded SO FAR, so a pair split across pages is
 * unannotated until the second page arrives and then labels both. That is the
 * honest reading — the client cannot count seats it has not fetched.
 */
export const withSeatOrdinals = (items: readonly EntitlementItem[]): readonly EntitlementItem[] => {
  const totals = new Map<string, number>()
  for (const item of items) {
    totals.set(item.itemNumber, (totals.get(item.itemNumber) ?? 0) + 1)
  }

  const seen = new Map<string, number>()

  return items.map(item => {
    const total = totals.get(item.itemNumber) ?? 1
    if (total < 2) {
      return item
    }

    const ordinal = (seen.get(item.itemNumber) ?? 0) + 1
    seen.set(item.itemNumber, ordinal)

    return { ...item, meta: formatMetaLine([item.meta, `Seat ${ordinal} of ${total}`]) }
  })
}
